if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      static API = 'https://api.reviews.io/product/review';
      static SHOP_API = 'https://api.reviews.io/merchant/reviews';
      static PER_PAGE = 100;
      static LABELS = {
        'Verarbeitungsqualität': 'Verarbeitung',
        'Preis-Leistungs-Verhältnis': 'Preis-Leistung',
      };

      connectedCallback() {
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.locale = document.documentElement.lang || 'de';
        this.list = this.querySelector('[data-list]');
        this.template = this.querySelector('[data-item]');
        this.isShop = this.hasAttribute('data-shop');
        this.moreButton = this.querySelector('[data-more]');
        this.moreButton?.addEventListener('click', this.toggleAll.bind(this));
        this.init();
      }

      async init() {
        try {
          const data = await this.fetchPage(1);
          if (!Number(data.stats?.count)) {
            if (!this.isShop) await this.renderEmpty();
            return;
          }
          this.renderSummary(data);
          this.dataset.loaded = '';
          this.renderItems(data.reviews.data, Number(this.dataset.preview));
          if (!this.isShop) await this.renderShopRating(this.querySelector('.product-reviews__summary [data-shop-rating]'));
        } catch (error) {
          console.error(error);
        }
      }

      async fetchPage(page) {
        if (this.isShop) return this.fetchShopPage(page);
        const params = new URLSearchParams({
          store: this.dataset.store,
          sku: this.dataset.sku,
          per_page: ProductReviews.PER_PAGE,
          page,
        });
        const response = await fetch(`${ProductReviews.API}?${params}`);
        const data = await response.json();
        this.page = data.reviews.current_page;
        this.lastPage = data.reviews.last_page;
        return data;
      }

      async fetchShopPage(page) {
        const params = new URLSearchParams({ store: this.dataset.store, per_page: 10, page });
        const response = await fetch(`${ProductReviews.SHOP_API}?${params}`);
        const data = await response.json();
        this.page = Number(data.page);
        this.lastPage = Number(data.total_pages);
        return {
          stats: { count: data.stats.total_reviews, average: data.stats.average_rating },
          reviews: { data: data.reviews.map((review) => ({ ...review, review: review.comments || '' })) },
        };
      }

      async renderEmpty() {
        const empty = this.querySelector('[data-empty]');
        if (!empty) return;
        await this.renderShopRating(empty.querySelector('[data-shop-rating]'));
        empty.hidden = false;
        this.dataset.empty = '';
      }

      async renderShopRating(element) {
        if (!element) return;
        const params = new URLSearchParams({ store: this.dataset.store, per_page: 1 });
        const response = await fetch(`${ProductReviews.SHOP_API}?${params}`);
        const { stats } = await response.json();
        element.textContent = element.textContent
          .replace('[average]', this.formatNumber(stats.average_rating, 2))
          .replace('[total]', Number(stats.total_reviews).toLocaleString(this.locale));
        element.hidden = false;
      }

      renderSummary(data) {
        const total = Number(data.stats.count);
        const average = Number(data.stats.average);

        this.renderStars(this.querySelector('.product-reviews__summary [data-stars]'), Math.round(average));
        this.querySelector('[data-average]').textContent = this.formatNumber(average, this.isShop ? 2 : 1);

        const countLabel = this.querySelector('[data-count-label]');
        countLabel.textContent = countLabel.textContent.replace('[total]', total.toLocaleString(this.locale));
        if (this.moreButton) {
          this.moreButton.textContent = this.moreButton.textContent.replace('[total]', total.toLocaleString(this.locale));
          this.moreLabel = this.moreButton.textContent;
        }

        const attributes = (data.ratings || [])
          .filter((rating) => !isNaN(parseFloat(rating.average_rating)))
          .map((rating) => `${this.attributeLabel(rating.rating_text)} ${this.formatNumber(rating.average_rating, 1)}`);
        const attributesElement = this.querySelector('[data-attributes]');
        if (!attributesElement) {
          this.total = total;
          return;
        }
        if (attributes.length) {
          attributesElement.textContent = attributes.join(' · ');
        } else {
          attributesElement.remove();
        }

        this.total = total;
      }

      renderItems(reviews, previewCount) {
        let visible = 0;
        reviews.forEach((review) => {
          const item = this.createItem(review);
          if (previewCount) {
            const show = visible < previewCount && review.review.trim() !== '';
            item.hidden = !show;
            if (show) {
              item.dataset.preview = '';
              visible++;
            }
          }
          this.list.append(item);
          this.initToggle(item);
        });
        if (this.moreButton && previewCount) this.moreButton.hidden = this.total <= visible;
      }

      initToggle(item) {
        const text = item.querySelector('[data-text]');
        const toggle = item.querySelector('[data-toggle]');
        if (!text) {
          toggle.remove();
          return;
        }
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', expanded);
          text.classList.toggle('is-expanded', expanded);
          toggle.textContent = expanded ? this.strings.readLess : this.strings.readMore;
        });
        this.textObserver ??= new ResizeObserver((entries) =>
          entries.forEach(({ target }) => {
            target.nextElementSibling.hidden =
              !target.classList.contains('is-expanded') && target.scrollHeight <= target.clientHeight;
          })
        );
        this.textObserver.observe(text);
      }

      createItem(review) {
        const item = this.template.content.firstElementChild.cloneNode(true);
        this.renderStars(item.querySelector('[data-stars]'), review.rating);

        const title = item.querySelector('[data-title]');
        if (review.title) {
          title.textContent = review.title;
        } else {
          title.remove();
        }

        const text = item.querySelector('[data-text]');
        if (review.review.trim()) {
          text.textContent = review.review.trim();
        } else {
          text.remove();
        }

        item.querySelector('[data-meta]').textContent = [
          review.product?.name,
          new Date(review.date_created).toLocaleDateString(this.locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          review.reviewer?.verified_buyer === 'yes' ? this.strings.verified : null,
        ]
          .filter(Boolean)
          .join(' · ');

        return item;
      }

      async toggleAll() {
        const expanded = this.moreButton.getAttribute('aria-expanded') !== 'true';
        this.moreButton.setAttribute('aria-expanded', String(expanded));
        this.moreButton.textContent = expanded ? this.strings.readLess : this.moreLabel;
        this.list
          .querySelectorAll('.product-reviews__item:not([data-preview])')
          .forEach((item) => (item.hidden = !expanded));

        if (!expanded) {
          const heading = this.closest('.product-reviews-wrapper')?.querySelector('.product-reviews__heading');
          heading?.scrollIntoView({ block: 'start' });
          heading?.focus({ preventScroll: true });
          return;
        }

        if (this.loadingAll) return;
        this.loadingAll = true;
        try {
          while (this.page < this.lastPage && this.moreButton.getAttribute('aria-expanded') === 'true') {
            const data = await this.fetchPage(this.page + 1);
            this.renderItems(data.reviews.data, 0);
          }
          if (this.moreButton.getAttribute('aria-expanded') !== 'true') {
            this.list
              .querySelectorAll('.product-reviews__item:not([data-preview])')
              .forEach((item) => (item.hidden = true));
          }
        } finally {
          this.loadingAll = false;
        }
      }

      renderStars(element, rating) {
        const star =
          '<path d="M12 2l2.94 6.96 7.06.31-5.5 4.9L18.18 22 12 18.27 5.82 22l1.68-7.83L2 9.27l7.06-.31z"/>';
        element.innerHTML = [1, 2, 3, 4, 5]
          .map(
            (i) =>
              `<svg class="product-reviews__star${i <= rating ? ' is-filled' : ''}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${star}</svg>`
          )
          .join('');
        element.setAttribute('role', 'img');
        element.setAttribute('aria-label', this.strings.stars.replace('[rating]', rating));
      }

      attributeLabel(text) {
        const label = text.replace(/^Wie bewertest Du (den|die|das)\s+/i, '').replace(/\?$/, '');
        return ProductReviews.LABELS[label] || label;
      }

      formatNumber(value, digits) {
        return Number(value).toLocaleString(this.locale, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        });
      }
    }
  );
}
